import React, { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Network, Wifi, Usb, Power } from "lucide-react";

const CONN_ICON = { network: Network, wifi: Wifi, usb: Usb };

/**
 * Devices tab — register door/desk fingerprint terminals. Each device gets an
 * API key; the terminal (or its middleware / USB agent) POSTs matched
 * fingerprint events to the webhook shown below.
 */
export default function AttendanceDevices({ branches, refreshKey, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", branch: "", connection: "network", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/devices");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Give the device a name"); return; }
    setBusy(true);
    try {
      await api.post("/attendance/devices", form);
      toast.success("Device registered — copy its API key into the terminal/agent");
      setOpen(false);
      setForm({ name: "", branch: "", connection: "network", notes: "" });
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle = async (d) => {
    try {
      await api.patch(`/attendance/devices/${d.id}`, { is_active: !d.is_active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete device "${d.name}"? Its API key stops working immediately.`)) return;
    try {
      await api.delete(`/attendance/devices/${d.id}`);
      toast.success("Device deleted");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyKey = (d) => {
    navigator.clipboard?.writeText(d.api_key);
    toast.success("API key copied");
  };

  const webhookUrl = `${API_BASE}/attendance/device-events`;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="text-xs text-gray-500">
          Terminals connect over network cable, Wi-Fi or USB (via a local agent) and push matched
          fingerprints to the webhook below.
        </div>
        <Button size="sm" onClick={() => setOpen(true)} className="ml-auto bg-emerald-900 hover:bg-emerald-800"
          data-testid="dev-add-button">
          <Plus size={14} className="mr-1.5" /> Register device
        </Button>
      </div>

      {/* Integration instructions */}
      <div className="bg-[#022C22] text-emerald-100 rounded-xl p-4 mb-4 text-xs font-mono overflow-x-auto"
        data-testid="dev-webhook-help">
        <div className="text-emerald-300/70 mb-1"># Door terminal / middleware / USB agent → push each matched fingerprint:</div>
        <div>POST {webhookUrl}</div>
        <div>Header: X-Device-Key: &lt;device API key&gt;</div>
        <div>{'Body: {"device_user_id": "1042"}  # the user ID matched on the scanner'}</div>
        <div className="text-emerald-300/70 mt-1"># Optional: "staff_number": "AB-017", "event_at": "2026-08-08T08:15:00"</div>
        <div className="text-emerald-300/70"># Works with ZKTeco / Hikvision / Suprema middleware and custom USB scanner agents.</div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-12 text-center">Loading devices…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No devices registered"
          hint="Register your door fingerprint terminal or desk scanner to get its API key." />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="dev-list">
          {items.map((d) => {
            const Icon = CONN_ICON[d.connection] || Network;
            return (
              <div key={d.id} className="bg-white border border-gray-200 rounded-xl p-4"
                data-testid={`dev-card-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-800 flex items-center justify-center shrink-0">
                    <Icon size={17} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{d.name}</div>
                    <div className="text-[11px] text-gray-500">
                      {d.branch || "All branches"} · {d.connection}
                      {d.last_seen_at ? ` · last seen ${d.last_seen_at.slice(0, 16).replace("T", " ")}` : " · never seen"}
                    </div>
                  </div>
                  <Badge tone={d.is_active ? "emerald" : "red"}>{d.is_active ? "active" : "disabled"}</Badge>
                </div>
                <div className="mt-3 flex items-center gap-1.5">
                  <code className="flex-1 text-[10px] bg-gray-50 border border-gray-200 rounded px-2 py-1.5 truncate">
                    {d.api_key}
                  </code>
                  <button onClick={() => copyKey(d)} title="Copy API key"
                    className="p-1.5 rounded text-gray-500 hover:bg-gray-100" data-testid={`dev-copy-${d.id}`}>
                    <Copy size={14} />
                  </button>
                  <button onClick={() => toggle(d)} title={d.is_active ? "Disable" : "Enable"}
                    className="p-1.5 rounded text-amber-600 hover:bg-amber-50" data-testid={`dev-toggle-${d.id}`}>
                    <Power size={14} />
                  </button>
                  <button onClick={() => remove(d)} title="Delete"
                    className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                    data-testid={`dev-delete-${d.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dev-dialog">
          <DialogHeader>
            <DialogTitle>Register fingerprint device</DialogTitle>
            <DialogDescription>
              A door terminal, desk scanner or USB reader agent. You'll get an API key to configure on it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Device name</Label>
              <Input value={form.name} placeholder="e.g. Main entrance terminal"
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 h-10" data-testid="dev-form-name" />
            </div>
            <div>
              <Label className="text-xs">Branch</Label>
              <select value={form.branch} data-testid="dev-form-branch"
                onChange={(e) => setForm((f) => ({ ...f, branch: e.target.value }))}
                className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm">
                <option value="">All branches</option>
                {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Connection</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {["network", "wifi", "usb"].map((c) => {
                  const Icon = CONN_ICON[c];
                  return (
                    <button key={c} type="button" onClick={() => setForm((f) => ({ ...f, connection: c }))}
                      data-testid={`dev-conn-${c}`}
                      className={`h-10 rounded-md border text-xs font-medium flex items-center justify-center gap-1.5 transition-colors ${
                        form.connection === c
                          ? "border-emerald-700 bg-emerald-50 text-emerald-800"
                          : "border-gray-300 text-gray-600 hover:border-gray-400"
                      }`}>
                      <Icon size={13} /> {c === "network" ? "Network cable" : c === "wifi" ? "Wireless" : "USB cable"}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes (model, location…)</Label>
              <Input value={form.notes} placeholder="e.g. ZKTeco F18, main door"
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="mt-1 h-10" data-testid="dev-form-notes" />
            </div>
            <Button onClick={create} disabled={busy}
              className="w-full h-11 bg-emerald-900 hover:bg-emerald-800" data-testid="dev-form-save">
              {busy ? "Registering…" : "Register device"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
